import { Injectable, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthStore, getTokenExpirationMs } from '@store/auth.facade';
import { AuthService } from '@services/auth/auth.service';
import { APIResponseGenericOfLoginResultResponseOfUserViewModel } from '@core/domain/models';

const WARNING_LEAD_MS = 2 * 60 * 1000;
const ACTIVITY_WINDOW_MS = 60 * 1000;
const CHECK_INTERVAL_MS = 15_000;
const ACTIVITY_THROTTLE_MS = 1_000;

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
    private readonly authStore = inject(AuthStore);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);

    readonly showWarningModal = signal(false);
    readonly secondsRemaining = signal(0);
    readonly refreshing = signal(false);

    private readonly lastActivityAt = signal(Date.now());

    private checkHandle: ReturnType<typeof setInterval> | null = null;
    private countdownHandle: ReturnType<typeof setInterval> | null = null;

    constructor() {
        this.registerActivityListeners();

        effect(() => {
            const token = this.authStore.token();
            if (token) {
                this.startChecking();
            } else {
                this.stopChecking();
                this.showWarningModal.set(false);
            }
        });
    }

    async stayLoggedIn(): Promise<void> {
        const success = await this.refreshToken();
        if (!success) {
            this.forceLogout();
        }
    }

    logoutNow(): void {
        this.forceLogout();
    }

    private registerActivityListeners(): void {
        let lastUpdate = 0;
        const onActivity = () => {
            const now = Date.now();
            if (now - lastUpdate >= ACTIVITY_THROTTLE_MS) {
                lastUpdate = now;
                this.lastActivityAt.set(now);
            }
        };

        window.addEventListener('mousemove', onActivity, { passive: true });
        window.addEventListener('mousedown', onActivity, { passive: true });
        window.addEventListener('keydown', onActivity, { passive: true });
        window.addEventListener('scroll', onActivity, { passive: true });
        window.addEventListener('touchstart', onActivity, { passive: true });
    }

    private hasRecentActivity(windowMs: number): boolean {
        return Date.now() - this.lastActivityAt() < windowMs;
    }

    private startChecking(): void {
        if (this.checkHandle) return;
        this.checkSession();
        this.checkHandle = setInterval(() => this.checkSession(), CHECK_INTERVAL_MS);
    }

    private stopChecking(): void {
        if (this.checkHandle) {
            clearInterval(this.checkHandle);
            this.checkHandle = null;
        }
        this.stopCountdown();
    }

    private async checkSession(): Promise<void> {
        const token = this.authStore.token();
        if (!token) {
            this.showWarningModal.set(false);
            return;
        }

        const expiresAt = getTokenExpirationMs(token);
        if (expiresAt === null) {
            return;
        }

        const remaining = expiresAt - Date.now();

        if (remaining <= 0) {
            this.forceLogout();
            return;
        }

        if (remaining <= WARNING_LEAD_MS) {
            if (this.refreshing()) {
                return;
            }

            if (this.hasRecentActivity(ACTIVITY_WINDOW_MS)) {
                const success = await this.refreshToken();
                if (!success) {
                    this.showWarningModal.set(true);
                    this.startCountdown(expiresAt);
                }
            } else {
                this.showWarningModal.set(true);
                this.startCountdown(expiresAt);
            }
        } else {
            this.showWarningModal.set(false);
            this.stopCountdown();
        }
    }

    private startCountdown(expiresAt: number): void {
        if (this.countdownHandle) return;

        const tick = () => {
            const remaining = expiresAt - Date.now();
            if (remaining <= 0) {
                this.forceLogout();
                return;
            }
            this.secondsRemaining.set(Math.ceil(remaining / 1000));
        };

        tick();
        this.countdownHandle = setInterval(tick, 1000);
    }

    private stopCountdown(): void {
        if (this.countdownHandle) {
            clearInterval(this.countdownHandle);
            this.countdownHandle = null;
        }
    }

    private async refreshToken(): Promise<boolean> {
        this.refreshing.set(true);
        try {
            const response = await firstValueFrom(
                this.authService.authRefresh<APIResponseGenericOfLoginResultResponseOfUserViewModel>(),
            );

            const token = response.result?.token;
            const user = response.result?.userInfo;

            if (!response.isSuccess || !token || !user) {
                return false;
            }

            this.authStore.updateAuth({ token, user });
            this.showWarningModal.set(false);
            this.stopCountdown();
            return true;
        } catch {
            return false;
        } finally {
            this.refreshing.set(false);
        }
    }

    private forceLogout(): void {
        this.authStore.clearAuth();
        this.showWarningModal.set(false);
        this.stopChecking();
        this.router.navigate(['/login']);
    }
}
